import { GetOptions, Invoice, QueryConstraint } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getNextPageParam } from "../utils/page-params";
import { fetchAllPages } from "../utils/fetch-all-pages";

const databaseService = serviceHost.getDatabaseService();
const invoiceRepository = repositoryHost.getInvoiceRepository(databaseService);

const ALL_OPEN_INVOICES_PAGE_SIZE = 300;

/**
 * Loads all still-open invoices (draft / sent) by auto-paging. Lazy via
 * `enabled` — used to populate the incoming-payment match picker.
 */
export const useAllOpenInvoices = (enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: ["invoices", "all-open"],
    enabled,
    queryFn: ({ pageParam }) =>
      invoiceRepository.getAll({
        queryConstraints: [
          { field: "status", operator: "in", value: ["draft", "sent"] },
        ],
        pagination: { limit: ALL_OPEN_INVOICES_PAGE_SIZE, cursor: pageParam },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam({
      pagination: { limit: ALL_OPEN_INVOICES_PAGE_SIZE },
    }),
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const invoices = useMemo(() => query.data?.pages.flat() ?? [], [query.data]);

  return {
    invoices,
    isLoading: enabled && (query.isLoading || query.hasNextPage),
  };
};

/**
 * Auto-pages every invoice matching `queryConstraints`, newest issued first.
 * Used by exports so the file covers the whole selected range.
 */
export const fetchAllInvoices = (
  queryConstraints: QueryConstraint<keyof Invoice & string>[] = [],
): Promise<Invoice[]> =>
  fetchAllPages<Invoice>((options) => invoiceRepository.getAll(options), {
    queryConstraints,
    orderBy: { field: "issuedAt", direction: "desc" },
  });

export const useInvoices = (options: GetOptions<Invoice>) => {
  return useInfiniteQuery({
    queryKey: ["invoices", "get", JSON.stringify(options)],
    queryFn: ({ pageParam }) =>
      invoiceRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

export const useInvoiceById = (invoiceId: string) => {
  return useQuery({
    queryKey: ["invoices", "get-by-id", invoiceId],
    enabled: invoiceId.length > 0,
    queryFn: () => invoiceRepository.get({ id: invoiceId }),
  });
};

export const useInvoicesBySoId = (soId: string) => {
  return useQuery({
    queryKey: ["invoices", "by-so", soId],
    enabled: soId.length > 0,
    queryFn: async () =>
      invoiceRepository.getAll({
        queryConstraints: [
          {
            field: "soId",
            operator: "==",
            value: soId,
          },
        ],
        orderBy: {
          field: "issuedAt",
          direction: "desc",
        },
        pagination: {
          limit: 100,
        },
      }),
  });
};

export const useInvoicesByPoId = (poId: string) => {
  return useQuery({
    queryKey: ["invoices", "by-po", poId],
    enabled: poId.length > 0,
    queryFn: async () =>
      invoiceRepository.getAll({
        queryConstraints: [
          {
            field: "poId",
            operator: "==",
            value: poId,
          },
        ],
        // Order by createdAt to match the existing (poId, createdAt) composite
        // index — there is no (poId, issuedAt) index, so ordering by issuedAt
        // would fail with a missing-index error and return no invoices.
        orderBy: {
          field: "createdAt",
          direction: "desc",
        },
        pagination: {
          limit: 100,
        },
      }),
  });
};

export const useInvoicesByInventoryId = (inventoryId: string) => {
  return useQuery({
    queryKey: ["invoices", "by-inventory", inventoryId],
    enabled: inventoryId.length > 0,
    queryFn: async () =>
      invoiceRepository.getAll({
        queryConstraints: [
          {
            field: "inventoryId",
            operator: "==",
            value: inventoryId,
          },
        ],
        orderBy: {
          field: "issuedAt",
          direction: "desc",
        },
        pagination: {
          limit: 200,
        },
      }),
  });
};
